import {useBooleanState} from '@wandb/weave/common/util/hooks';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Radio} from '@wandb/weave/components/Radio';
import React, {useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {OrganizationPrefix} from '../types';
import {useWorkspaceSettingsContext} from '../WorkspaceSettingsContextProvider';
import {getWorkspaceLayoutSettingWithDefaults} from './getWorkspaceLayoutSettings';

export const WorkspaceLayoutAutoOrganizePrefix = () => {
  const {workspaceLayout, updateLayoutSettingAndOrganizePrefix} =
    useWorkspaceSettingsContext();
  const {autoOrganizePrefix, shouldAutoGeneratePanels} = useMemo(
    () => getWorkspaceLayoutSettingWithDefaults(workspaceLayout),
    [workspaceLayout]
  );

  const {
    state: isProtectedSettingsLocked,
    toggle: toggleProtectedSettingsLock,
  } = useBooleanState(true);

  if (shouldAutoGeneratePanels === 'pending') {
    return null;
  }

  return (
    <ProtectedSetting
      isProtectedSettingsLocked={isProtectedSettingsLocked}
      shouldAutoGeneratePanels={shouldAutoGeneratePanels}
      toggleProtectedSettingsLock={toggleProtectedSettingsLock}>
      <fieldset className="text-moon-800">
        <Radio.Root
          aria-label="Grouping sections by prefix"
          name="Organize panels"
          disabled={isProtectedSettingsLocked}
          onValueChange={value => {
            const numVal = Number(value);
            updateLayoutSettingAndOrganizePrefix(numVal);
          }}
          value={autoOrganizePrefix.toString()}>
          <div className="flex items-center justify-start">
            <Radio.Item
              id="organize-panels-first-prefix"
              value={OrganizationPrefix.FirstPrefix.toString()}>
              <Radio.Indicator />
            </Radio.Item>
            <label htmlFor="organize-panels-first-prefix">
              <span className="mx-8">Group first prefix</span>
              <span className="text-moon-500">
                e.g. metric "a/b/c/d" → section a
              </span>
            </label>
          </div>
          <div className="flex items-center justify-start">
            <Radio.Item
              id="organize-panels-last-prefix"
              value={OrganizationPrefix.LastPrefix.toString()}>
              <Radio.Indicator />
            </Radio.Item>
            <label
              htmlFor="organize-panels-last-prefix"
              className="align-center flex">
              <span className="mx-8">Group last prefix</span>
              <span className="text-moon-500">
                e.g. metric "a/b/c/d" → section a/b/c
              </span>
            </label>
          </div>
        </Radio.Root>
      </fieldset>
    </ProtectedSetting>
  );
};

const ProtectedSetting = ({
  isProtectedSettingsLocked,
  shouldAutoGeneratePanels,
  toggleProtectedSettingsLock,
  children,
}: {
  isProtectedSettingsLocked: boolean;
  shouldAutoGeneratePanels: boolean;
  toggleProtectedSettingsLock: () => void;
  children: React.ReactNode;
}) => {
  return (
    <div className="rounded bg-moon-100 px-16 py-12">
      <div className="mb-8 flex justify-between gap-10">
        <div>
          <p className="text-base font-semibold leading-8">
            Section organization
          </p>
          <p className="text-sm text-moon-500">
            {shouldAutoGeneratePanels
              ? 'All existing sections and panels will be removed, and your workspace will be repopulated with updated section names.'
              : 'All existing sections and panels will be removed.'}
          </p>
        </div>
        <Button
          icon={isProtectedSettingsLocked ? 'lock-closed' : 'lock-open'}
          variant="secondary"
          onClick={toggleProtectedSettingsLock}
        />
      </div>
      <div
        className={twMerge(
          'transition-opacity',
          isProtectedSettingsLocked && 'opacity-30'
        )}>
        {children}
      </div>
    </div>
  );
};
